import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, inject, input, ViewChild } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, AbstractControl } from '@angular/forms';
import { pattern, FormatterService } from '@xmedical/core';
import {MatFormFieldAppearance, MatFormFieldModule} from '@angular/material/form-field';
import {ThemePalette} from '@angular/material/core';
import {MatInputModule} from '@angular/material/input';
import {ReactiveFormsModule} from '@angular/forms';

@Component({
    selector: 'xui-mat-form-input',
    templateUrl: './app-mat-form-input.component.html',
    styleUrls: ['./app-mat-form-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatFormFieldModule, MatInputModule, ReactiveFormsModule]
})
export class AppMatFormInputComponent implements AfterViewInit {

    private formatter = inject(FormatterService);

    readonly appearance = input<MatFormFieldAppearance>('fill');
    readonly color = input<ThemePalette>('primary');
    readonly maxLength = input(200);
    readonly pattern = input<pattern>('default');
    readonly parentForm = input<UntypedFormGroup | undefined>();
    readonly name = input('');
    readonly label = input('');
    readonly type = input<inputType>('text');
    readonly placeholder = input('');
    readonly helpText = input('');
    readonly errorMessage = input('');
    readonly readonly = input(false);
    @ViewChild('input') input: ElementRef;

    private get element(): UntypedFormControl | null {
        const parentForm = this.parentForm();
        const name = this.name();
        if (!parentForm || !name) {
            return null;
        }
        return parentForm.get(name) as UntypedFormControl | null;
    }

    ngAfterViewInit(): void {
        // Render the control's current value through the formatter. The reactive-forms
        // control is the single source of truth — never overwrite it from the wrapper.
        const element = this.element;
        if (element) {
            this.format(element.value);
        }
    }

    get required(): boolean {
        const element = this.element;
        const validator = element?.validator ? element.validator({} as AbstractControl) : null;
        return !!(validator && validator.required);
    }

    get fieldValid(): boolean {
      const element = this.element;
      const required = this.required || element?.errors !== null;
      if (!required || this.readonly()) { return true; }
      return !element?.invalid;
    }

    blur(value: string) {
        this.format(value);
    }

    keyUp(value: string) {
        this.format(value);
    }

    paste(value: string) {
        this.format(value);
    }

    private format(value: string) {
        this.input.nativeElement.value = this.formatter.format(value ?? '', this.pattern());
    }

}

export type inputType =
    | 'text'
    | 'tel'
    | 'email'
    | 'number';
